import { useCallback, useEffect } from "react";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type ComposerThreadTarget,
  type ComposerThreadDraftState,
} from "../../composerDraftStore";
import { type DraftId } from "../../draftId";
import { hydrateFilesFromPersisted } from "../../composerDraftAttachments";
import { composerFileAttachmentStore } from "../../composerFileAttachmentStore";
import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { toastManager } from "../ui/toast";
import { useComposerImageAttachments } from "./useComposerImageAttachments";

interface UseChatComposerAttachmentsInput {
  readonly composerDraftTarget: ComposerThreadTarget;
  readonly composerImages: ComposerImageAttachment[];
  readonly composerFiles: readonly ComposerFileAttachment[];
  readonly routeKind: "server" | "draft";
  readonly draftId: DraftId | null;
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId | null;
  readonly pendingUserInputCount: number;
  readonly addComposerDraftImages: (
    target: ComposerThreadTarget,
    images: ComposerImageAttachment[],
  ) => void;
  readonly addComposerDraftFiles: (
    target: ComposerThreadTarget,
    files: ComposerFileAttachment[],
  ) => void;
  readonly removeComposerDraftImage: (target: ComposerThreadTarget, imageId: string) => void;
  readonly setComposerDraftFiles: (
    target: ComposerThreadTarget,
    files: ComposerFileAttachment[],
  ) => void;
  readonly getComposerDraft: (target: ComposerThreadTarget) => ComposerThreadDraftState | null;
  readonly setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  readonly focusComposer: () => void;
}

export function useChatComposerAttachments(input: UseChatComposerAttachmentsInput) {
  const {
    composerDraftTarget,
    composerImages,
    composerFiles,
    routeKind,
    draftId,
    environmentId,
    activeThreadId,
    pendingUserInputCount,
    addComposerDraftImages,
    addComposerDraftFiles,
    removeComposerDraftImage,
    setComposerDraftFiles,
    getComposerDraft,
    setThreadError,
    focusComposer,
  } = input;
  const addComposerImagesToDraft = useCallback(
    (images: ComposerImageAttachment[]) => {
      addComposerDraftImages(composerDraftTarget, images);
    },
    [addComposerDraftImages, composerDraftTarget],
  );
  const addComposerFilesToDraft = useCallback(
    (files: ComposerFileAttachment[]) => {
      addComposerDraftFiles(composerDraftTarget, files);
    },
    [addComposerDraftFiles, composerDraftTarget],
  );
  const removeComposerImageFromDraft = useCallback(
    (imageId: string) => {
      removeComposerDraftImage(composerDraftTarget, imageId);
    },
    [composerDraftTarget, removeComposerDraftImage],
  );
  const removeComposerFileFromDraft = useCallback(
    (fileId: string) => {
      setComposerDraftFiles(
        composerDraftTarget,
        composerFiles.filter((file) => file.id !== fileId),
      );
      void releaseUnreferencedComposerFile(fileId);
    },
    [composerDraftTarget, composerFiles, setComposerDraftFiles],
  );

  const imageAttachments = useComposerImageAttachments({
    images: composerImages,
    files: composerFiles,
    scopeKey:
      routeKind === "draft"
        ? `draft:${draftId ?? "pending"}`
        : `thread:${environmentId}:${activeThreadId ?? "pending"}`,
    isDisabled: () => activeThreadId === null,
    blockedReason:
      pendingUserInputCount > 0 ? "Attach files after answering plan questions." : null,
    onBlocked: (message) => {
      toastManager.add({ type: "error", title: message });
    },
    onAddImages: addComposerImagesToDraft,
    onAddFiles: addComposerFilesToDraft,
    onError: (message) => setThreadError(activeThreadId, message),
    onAfterDrop: focusComposer,
  });

  useEffect(() => {
    const persisted = composerFiles.filter((attachment) => attachment.file === null);
    if (persisted.length === 0) return;
    let cancelled = false;
    const expectedIds = persisted.map((attachment) => attachment.id);
    void hydrateFilesFromPersisted(persisted, composerFileAttachmentStore)
      .then((hydrated) => {
        if (cancelled || hydrated === null) return;
        const current = getComposerDraft(composerDraftTarget)?.files ?? [];
        const currentPendingIds = current
          .filter((attachment) => attachment.file === null)
          .map((attachment) => attachment.id);
        if (
          currentPendingIds.length !== expectedIds.length ||
          currentPendingIds.some((id, index) => id !== expectedIds[index])
        )
          return;
        const restoredById = new Map(hydrated.map((attachment) => [attachment.id, attachment]));
        setComposerDraftFiles(
          composerDraftTarget,
          current.map((attachment) => restoredById.get(attachment.id) ?? attachment),
        );
      })
      .catch(() => {
        if (!cancelled) setThreadError(activeThreadId, "Could not restore draft attachments.");
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeThreadId,
    composerDraftTarget,
    composerFiles,
    getComposerDraft,
    setComposerDraftFiles,
    setThreadError,
  ]);

  return {
    imageAttachments,
    addComposerImageFiles: imageAttachments.addFiles,
    removeComposerImageFromDraft,
    removeComposerFileFromDraft,
  };
}
